import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import MathText from "@/components/MathText";
import { Lock, CheckCircle2, Circle, BookOpen, MessagesSquare } from "lucide-react";
import { gradeShort } from "@/config";

export default function Path() {
  const { studentId } = useOutletContext();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);

  useEffect(() => { api.get(`/students/${studentId}/plan`).then(({ data }) => setPlan(data)); }, [studentId]);
  if (!plan) return <Spinner label="Loading your path" />;

  if (plan.diagnostic_needed || !plan.entries?.length) {
    return (
      <Card className="p-8 text-center">
        <p className="text-[var(--ns-muted)]">Take the diagnostic to generate your learning path.</p>
        <Button className="mt-4 mx-auto" onClick={() => nav(`/student/${studentId}/diagnostic`)}>Start diagnostic</Button>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-1">Your learning path</h1>
      <p className="text-[var(--ns-muted)] mb-6">Ordered by what you need first — foundations before the next big idea.</p>

      <div className="relative pl-6">
        <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-[var(--ns-border)]" />
        <div className="grid gap-4">
          {plan.entries.map((e) => {
            const icon = e.mastered ? <CheckCircle2 size={20} className="text-[#2A8555]" /> : e.status === "locked" ? <Lock size={18} className="text-[var(--ns-subtle)]" /> : <Circle size={18} className="text-[var(--ns-primary)]" />;
            return (
              <div key={e.skill_id} className="relative" data-testid={`path-skill-${e.skill_id}`}>
                <div className="absolute -left-6 top-4 bg-[var(--ns-bg)] rounded-full">{icon}</div>
                <Card className="p-4" style={{ opacity: e.status === "locked" && !e.mastered ? 0.65 : 1 }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-bold text-[var(--ns-text)]">{e.name}</span>
                        {e.is_gap && !e.mastered && <Pill tone="gap">Gap</Pill>}
                        {e.mastered && <Pill tone="mastered">Mastered</Pill>}
                      </div>
                      <div className="text-xs text-[var(--ns-subtle)] mt-0.5">{gradeShort(e.grade)} · {e.strand}</div>
                    </div>
                  </div>
                  <p className="text-sm text-[var(--ns-muted)] mt-2"><MathText text={e.why_it_matters} /></p>
                  {(e.status === "unlocked" || e.mastered) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="subtle" onClick={() => nav(`/student/${studentId}/lesson/${e.skill_id}`)}><BookOpen size={15} /> Lesson</Button>
                      <Button variant="outline" onClick={() => nav(`/student/${studentId}/practice/${e.skill_id}`)}>Practice</Button>
                      {!e.mastered && <Button variant="outline" onClick={() => nav(`/student/${studentId}/mastery/${e.skill_id}`)}>Mastery</Button>}
                      <Button variant="ghost" onClick={() => nav(`/student/${studentId}/tutor/${e.skill_id}`)}><MessagesSquare size={15} /> Tutor</Button>
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
